import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X as XIcon, Cookie, ShieldCheck, BarChart3 } from "lucide-react";
import { CONSENT_KEY, readConsent, saveConsent, applyConsentToGtag } from "@/lib/consent";

/**
 * RGPD Consent banner with Google Consent Mode v2 integration.
 * - First visit → banner visible with 3 CTAs: Tout accepter · Tout refuser · Personnaliser
 * - Choice persisted in localStorage; can be reopened from the footer link
 * - Analytics category toggles gtag('consent', 'update', {analytics_storage})
 */
export default function CookieBanner() {
    const [visible, setVisible] = useState(false);
    const [customize, setCustomize] = useState(false);
    const [analytics, setAnalytics] = useState(false);

    useEffect(() => {
        const existing = readConsent();
        if (!existing) {
            setVisible(true);
        } else {
            applyConsentToGtag(existing);
            setAnalytics(!!existing.analytics);
        }
        const reopen = () => {
            const cur = readConsent() || { analytics: false };
            setAnalytics(!!cur.analytics);
            setCustomize(true);
            setVisible(true);
        };
        window.addEventListener("fol:open-consent", reopen);
        return () => window.removeEventListener("fol:open-consent", reopen);
    }, []);

    const persist = (choice) => {
        saveConsent(choice);
        setVisible(false);
        setCustomize(false);
    };

    const acceptAll = () => persist({ analytics: true });
    const rejectAll = () => persist({ analytics: false });
    const saveCustom = () => persist({ analytics });

    if (!visible) return null;

    return (
        <div
            className="fixed inset-x-3 bottom-3 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-lg z-50 rounded-3xl border border-border/60 bg-card/95 backdrop-blur p-6 shadow-2xl"
            data-testid="cookie-banner"
            role="dialog"
            aria-live="polite"
            aria-labelledby="cookie-banner-title"
        >
            <button
                onClick={() => setVisible(false)}
                aria-label="Fermer"
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
                data-testid="cookie-banner-close"
            >
                <XIcon className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3 mb-3">
                <Cookie className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                    <p className="text-xs uppercase tracking-widest text-primary font-medium">Vos préférences</p>
                    <h3 id="cookie-banner-title" className="font-serif text-lg leading-tight mt-1">Cookies & confidentialité</h3>
                </div>
            </div>

            {!customize ? (
                <>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        Nous utilisons un <strong className="text-foreground/90">cookie de session</strong> nécessaire au bon fonctionnement du site
                        et, avec votre accord, un outil <strong className="text-foreground/90">de mesure d'audience anonymisée</strong> (Google Analytics)
                        pour comprendre comment améliorer votre expérience.
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                        Consultez notre <Link to="/confidentialite" className="underline decoration-primary/40 underline-offset-4">politique de confidentialité</Link>. Vous pouvez modifier ce choix à tout moment depuis le pied de page.
                    </p>
                    <div className="mt-5 flex flex-col sm:flex-row gap-2">
                        <button
                            onClick={acceptAll}
                            className="rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:opacity-90 flex-1"
                            data-testid="cookie-accept-all"
                        >
                            Tout accepter
                        </button>
                        <button
                            onClick={rejectAll}
                            className="rounded-full border border-border/60 bg-card text-foreground px-5 py-2.5 text-sm font-medium hover:bg-muted/50 flex-1"
                            data-testid="cookie-reject-all"
                        >
                            Tout refuser
                        </button>
                        <button
                            onClick={() => setCustomize(true)}
                            className="rounded-full text-sm text-foreground/70 underline decoration-primary/40 underline-offset-4 hover:decoration-primary px-3 py-2"
                            data-testid="cookie-customize"
                        >
                            Personnaliser
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                        Choisissez précisément les catégories de cookies que vous acceptez. Les cookies fonctionnels sont toujours actifs — ils sont nécessaires à l'utilisation du site.
                    </p>
                    {/* Functional (locked ON) */}
                    <div className="flex items-start gap-3 rounded-2xl border border-border/40 bg-muted/20 p-3 mb-2" data-testid="consent-cat-functional">
                        <ShieldCheck className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">Cookies fonctionnels</span>
                                <span className="text-xs text-primary font-medium">Toujours actifs</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                                Session, authentification, préférences d'affichage. Indispensables au fonctionnement du site.
                            </p>
                        </div>
                    </div>
                    {/* Analytics (toggleable) */}
                    <label className="flex items-start gap-3 rounded-2xl border border-border/40 bg-background p-3 cursor-pointer hover:bg-muted/30 transition-colors" data-testid="consent-cat-analytics">
                        <input
                            type="checkbox"
                            checked={analytics}
                            onChange={(e) => setAnalytics(e.target.checked)}
                            className="mt-1 h-4 w-4 rounded border-input accent-primary shrink-0 cursor-pointer"
                            data-testid="consent-analytics-checkbox"
                        />
                        <div className="flex-1">
                            <div className="flex items-center gap-2">
                                <BarChart3 className="w-4 h-4 text-primary" />
                                <span className="text-sm font-medium">Mesure d'audience (Google Analytics)</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                                Statistiques anonymisées d'utilisation (pages consultées, provenance). IP anonymisée. Aucune publicité, aucun partage à des tiers.
                            </p>
                        </div>
                    </label>
                    <div className="mt-5 flex flex-col sm:flex-row gap-2">
                        <button
                            onClick={saveCustom}
                            className="rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:opacity-90 flex-1"
                            data-testid="cookie-save-custom"
                        >
                            Enregistrer mes choix
                        </button>
                        <button
                            onClick={acceptAll}
                            className="rounded-full border border-border/60 bg-card text-foreground px-5 py-2.5 text-sm font-medium hover:bg-muted/50 flex-1"
                            data-testid="cookie-accept-all-custom"
                        >
                            Tout accepter
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
